/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type Badges,
    DefinitionList,
    ElevatedCard,
    Flex,
    FullBleed,
    HorizontalDivider,
    IconButton,
    IconInfo,
    Modal,
    Stack,
    Subtitle3,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import { AlternativeCard, PricingInfo } from '@phx/myphx-lib';

import { getFragment } from '../../../../graphql';
import {
    type CashOfferInfoFragment,
    type ClaimGeneratedPrescriberOrderInfoFragment,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type GetAltProductOffersQuery,
    type GetClaimV2Query,
    type InsuranceOfferInfoFragment,
    type MedicationInfoFragment,
    type OfferPricingDetail,
    type PatientInfoFragment,
    type PatientSavingsRecommendationInfoFragment,
    type PlanSavingsRecommendationInfoFragment,
    type ProductInfoFragment,
    type ProviderOffersInfoFragment,
} from '../../../../graphql/generated/graphql';
import { useFlags } from '../../../../hooks/use-flags';
import { useRecommendationBadge } from '../../../../hooks/use-recommendation-badge';
import { getPrimaryProvider } from '../../../../util';
import {
    getMedicationDescription,
    getOrderQuantity,
} from '../../../../util/cabinet/prescriber-orders';
import { getPriceableOffers } from '../../../../util/offers/get-priceable-offers';
import { ContactUsWidget } from '../../../common/ContactUsWidget';
import { InfoHeading } from '../../../common/InfoHeading';
import { ProductDetails } from '../../../preview-details/components/ProductDetails';
import {
    type TranslationBlocks,
    asDefinitionListItem,
} from '../../../utility/AsDefinitionListItem';

import { EstimatedPriceSection } from './EstimatedPriceSection';
import { AltsPrescriberSection } from './PrescriberSection';

export type AltOffer =
    | (Pick<
          InsuranceOfferInfoFragment,
          | '__typename'
          | 'patientResponsibilityCost'
          | 'planPaysAmount'
          | 'pricingDetail'
      > &
          Partial<Pick<InsuranceOfferInfoFragment, 'pharmacyProvider'>>)
    | Pick<
          CashOfferInfoFragment,
          '__typename' | 'patientResponsibilityCost' | 'pricingDetail'
      >;
export type ProductConnection =
    | NonNullable<
          GetAltProductOffersQuery['prescriberOrder']
      >['fillRecommendationsConnection']
    | NonNullable<
          GetClaimV2Query['claim']
      >['fill']['alternativeProductsConnection'];

export type AltProduct = {
    product: ProductInfoFragment;
    pricing: OfferPricingDetail[];
    planSavings: number;
    patientSavings: number;
};

type AlternativesProps = {
    patient?: PatientInfoFragment;
    product: ProductInfoFragment;
    cheapestOffer: AltOffer;
    prescriberOrder?:
        | ClaimGeneratedPrescriberOrderInfoFragment
        | DigitallyReceivedPrescriberOrderInfoFragment;
    pharmacyName?: string;
    products: AltProduct[];
    recommendations: (
        | PlanSavingsRecommendationInfoFragment
        | PatientSavingsRecommendationInfoFragment
    )[];
};
export const Alternatives = (props: AlternativesProps) => {
    const {
        products,
        product,
        cheapestOffer,
        prescriberOrder,
        pharmacyName,
        recommendations,
    } = props;
    const { t } = useTranslation();

    const dialogContentBlocks: TranslationBlocks = t<
        string,
        { returnObjects: true },
        TranslationBlocks
    >('alternatives.infoDialog.content', { returnObjects: true });

    if (!prescriberOrder) {
        return null;
    }

    const quantity = getOrderQuantity(prescriberOrder) ?? product.modeQuantity;
    const productDescription = getMedicationDescription(prescriberOrder);
    const prescriber = getFragment(prescriberOrder.prescriber);
    const [selectedProduct, setSelectedProduct] = useState<
        ProductInfoFragment | MedicationInfoFragment | undefined
    >(undefined);
    const [openedAltCard, { open: openAltCard, close: closeAltCard }] =
        useDisclosure(false);
    const [
        openedEstPriceInfo,
        { open: openEstPriceInfo, close: closeEstPriceInfo },
    ] = useDisclosure(false);
    const handleCardClick = (
        product: ProductInfoFragment | MedicationInfoFragment
    ) => {
        setSelectedProduct(product);
        openAltCard();
    };

    const pharmacyProvider =
        cheapestOffer.__typename === 'InsuranceOffer'
            ? getFragment(cheapestOffer.pharmacyProvider)
            : undefined;

    const controlledSubstanceCode =
        prescriberOrder.__typename === 'DigitallyReceivedPrescriberOrder'
            ? getFragment(prescriberOrder.authorizedMedication)
                  .controlledSubstanceCode
            : null;

    const { recommendationAlternativesEnabled } = useFlags();

    const mapBadge = useRecommendationBadge();
    const recommendationsByOffer = Object.values(
        recommendations.reduce(
            (acc, recommendation) => {
                const providerOffers = getFragment(
                    recommendation.providerOffers
                );
                const offersId = providerOffers?.id;
                const badge = mapBadge(recommendation);
                if (offersId && badge) {
                    const badges = acc[offersId]?.badges ?? [];
                    acc[offersId] = {
                        badges: [...badges, badge],
                        providerOffers,
                    };
                }
                return acc;
            },
            {} as Record<
                string,
                { badges: Badges[]; providerOffers: ProviderOffersInfoFragment }
            >
        )
    );
    return (
        <Stack>
            <ElevatedCard>
                <Stack gap="sm">
                    <Stack gap="xxxs">
                        <ProductDetails
                            controlledSubstanceCode={controlledSubstanceCode}
                            product={product}
                            productDescription={productDescription}
                            quantity={quantity}
                        />
                    </Stack>
                    <Stack gap="xs">
                        <HorizontalDivider />
                        <PricingInfo {...cheapestOffer.pricingDetail} />
                        <Flex justify="space-between" align="center">
                            <Subtitle3>
                                {t('alternatives.estimatedPriceAt', {
                                    name: pharmacyName,
                                })}
                            </Subtitle3>
                            {pharmacyProvider ? (
                                <IconButton
                                    Icon={IconInfo}
                                    onClick={openEstPriceInfo}
                                    withStyles={false}
                                    size="sm"
                                />
                            ) : undefined}
                        </Flex>
                    </Stack>
                </Stack>
            </ElevatedCard>

            <FullBleed breakpoint="md" color="gray">
                <Stack role="list">
                    <InfoHeading
                        title={t('alternatives.title')}
                        informationDialog={{
                            title: t('alternatives.infoDialog.title'),
                            content: (
                                <DefinitionList
                                    compact={false}
                                    items={dialogContentBlocks.map(
                                        asDefinitionListItem
                                    )}
                                />
                            ),
                        }}
                    />
                    <Stack gap="sm">
                        {recommendationAlternativesEnabled
                            ? recommendationsByOffer.map(
                                  ({ providerOffers, badges }) => {
                                      const medication = getFragment(
                                          providerOffers?.medication
                                      );
                                      if (!medication || !badges.length) {
                                          return null;
                                      }
                                      const pricing = getPriceableOffers(
                                          providerOffers
                                      ).map((offer) => offer.pricingDetail);

                                      const pharmacyName =
                                          getPrimaryProvider(
                                              providerOffers
                                          )?.name;
                                      return (
                                          <AlternativeCard
                                              isControlledSubstance={
                                                  !!controlledSubstanceCode
                                              }
                                              role="listitem"
                                              key={medication.id}
                                              onClick={() =>
                                                  handleCardClick(medication)
                                              }
                                              product={{
                                                  name: medication.name,
                                                  description: `${medication.strengthDisplayText}`,
                                              }}
                                              pricing={pricing}
                                              pharmacyName={pharmacyName}
                                              badges={badges}
                                          />
                                      );
                                  }
                              )
                            : products.map((productInfo) => (
                                  <AlternativeCard
                                      isControlledSubstance={
                                          !!controlledSubstanceCode
                                      }
                                      role="listitem"
                                      key={productInfo.product.id}
                                      onClick={() =>
                                          handleCardClick(productInfo.product)
                                      }
                                      {...productInfo}
                                      product={{
                                          name: productInfo.product.name,
                                          description: `${productInfo.product.strengthDisplayText}`,
                                      }}
                                  />
                              ))}
                    </Stack>
                </Stack>
            </FullBleed>
            <Stack gap="lg">
                <AltsPrescriberSection prescriber={prescriber} />
                <HorizontalDivider />
                <ContactUsWidget />
            </Stack>
            <Modal
                onClose={closeAltCard}
                opened={openedAltCard}
                title={t('alternatives.newPrescription.switchTo', {
                    name: selectedProduct?.name ?? '',
                })}
            >
                <AltsPrescriberSection
                    controlledSubstanceCode={controlledSubstanceCode}
                    prescriber={prescriber}
                />
            </Modal>
            {pharmacyProvider ? (
                <Modal
                    onClose={closeEstPriceInfo}
                    opened={openedEstPriceInfo}
                    title={t('alternatives.estimatedPrice.header')}
                >
                    <EstimatedPriceSection
                        pharmacyProvider={pharmacyProvider}
                    />
                </Modal>
            ) : undefined}
        </Stack>
    );
};
